import { defineStore } from "pinia";
import router from "../router/index";

const SESSION_EXPIRE_KEY = "U2EDC.sessionExpireAt";
const SESSION_SYNC_KEY = "U2EDC.sessionSync"; // 다른 탭 동기화 트리거용
const SESSION_MINUTES = Number(
  import.meta.env.VITE_SESSION_MINUTES ?? (import.meta.env.DEV ? 1000 : 30),
);
const SESSION_MAX_MINUTES = Number(
  import.meta.env.VITE_SESSION_MAX_MINUTES ?? (import.meta.env.DEV ? 1000 : 30),
);

export const useUserStateStore = defineStore("userState", {
  state: () => ({
    user: {
      SystemUserID: null,
      APIKey: "",
      CellNumber: "",
      DepartmentName: "",
      DutyID: null,
      DutyName: "",
      EMailPost: "",
      EMailPre: "",
      InstitutionID: null,
      InstitutionNameLc: "",
      LicenseNumber: "",
      QuestionText: "",
      UserID: "",
      UserNameEn: "",
      UserNameLc: "",
      UserPW: "",
      UserType: "",
      LastPWChangeDay: "",
      _InstitutionCode: "",
    },
    AssociationID: 1,
    alarmCnt: 0,

    // ✅ 세션 만료시각(ms)
    sessionExpireAt: 0,

    // ✅ 남은 시간(ms) - UI용
    sessionRemainingMs: 0,

    // ✅ 내부 타이머 id
    _sessionTimerId: null,
    _logoutHandled: false,
    // ✅ 10분 경고 다이얼로그 표시 여부(UI용)
    showSessionWarning: false,

    // ✅ 중복 오픈 방지 (세션당 1회)
    _warningShown: false,
  }),
  actions: {
    /* 회원 관련 Actions 시작 */
    setUser(userData) {
      this.user = userData;
      this._logoutHandled = false;
      // ✅ 로그인 성공 시 세션 30분 시작
      this.startOrExtendSession(SESSION_MINUTES);
    },

    setAssociationID(id) {
      this.AssociationID = id;
    },

    clearUser() {
      this.user = {
        APIKey: "",
        AssociationID: null,
        CellNumber: "",
        DepartmentName: "",
        DutyID: null,
        DutyName: "",
        EMailPost: "",
        EMailPre: "",
        InstitutionID: null,
        InstitutionNameLc: "",
        LicenseNumber: "",
        QuestionText: "",
        UserID: "",
        UserNameEn: "",
        UserNameLc: "",
        UserPW: "",
        UserType: "",
        LastPWChangeDay: "",
        _InstitutionCode: "",
      };
      sessionStorage.removeItem("userState");
      localStorage.setItem("U2EDC.sessionClear", Date.now());

      // ✅ 세션 관련 값 정리
      this.sessionExpireAt = 0;
      this.sessionRemainingMs = 0;
      this._stopSessionTimer();

      // ✅ expireAt은 localStorage에 있으니 이것도 삭제
      localStorage.removeItem(SESSION_EXPIRE_KEY);

      // ✅ 추가: 세션 동기화 트리거
      localStorage.setItem(SESSION_SYNC_KEY, String(Date.now()));
    },

    setAlarmCnt(cnt) {
      this.alarmCnt = cnt;
    },
    /* 회원 관련 Actions 끝 */

    // ---------------------------
    // ✅ 여기부터 세션 기능 추가
    // ---------------------------
    initSessionControl({ resetOnNewTab = true } = {}) {
      // 0) 타이머/리스너 기본 세팅
      const saved = localStorage.getItem(SESSION_EXPIRE_KEY);
      this.sessionExpireAt = saved ? Number(saved) : 0;

      // 1) storage 리스너 먼저 등록 (중복 방지)
      // - 만료로 인한 early return 이후에도 다른 탭 동기화가 동작해야 하므로 앞으로 이동
      if (!this._storageListenerBound) {
        this._storageListenerBound = true;

        window.addEventListener("storage", (event) => {
          if (event.key === "U2EDC.sessionClear") {
            this._logoutAndGoLogin();
            return;
          }

          if (
            event.key === SESSION_EXPIRE_KEY ||
            event.key === SESSION_SYNC_KEY
          ) {
            const v = localStorage.getItem(SESSION_EXPIRE_KEY);
            this.sessionExpireAt = v ? Number(v) : 0;
            this._updateSessionRemaining();

            const expired =
              this.sessionExpireAt > 0 && this.sessionExpireAt <= Date.now();
            if (expired) this._logoutAndGoLogin();
          }
        });
      }

      // ✅ 2) 브라우저 재오픈 시 세션 처리
      if (resetOnNewTab) {
        if (this.sessionExpireAt > 0) {
          // 브라우저 닫힌 동안 만료된 경우 → 로그아웃
          if (this.sessionExpireAt <= Date.now()) {
            this._logoutAndGoLogin();
            return;
          }
          // 아직 유효하면 지금부터 SESSION_MINUTES분으로 재연장
          const newExpireAt = Date.now() + SESSION_MINUTES * 60 * 1000;
          this.sessionExpireAt = newExpireAt;

          localStorage.setItem(SESSION_EXPIRE_KEY, String(newExpireAt));
          localStorage.setItem(SESSION_SYNC_KEY, String(Date.now())); // ✅ 기존 탭 즉시 동기화 트리거
        }
      }

      // 3) UI 업데이트 + 타이머
      this._updateSessionRemaining();
      this._startSessionTimer();
    },

    /**
     * 로그인 연장 버튼에서 호출: 남은 시간과 상관없이 "지금부터 30분"
     */
    extendSession(minutes = 30) {
      if (!this.isAuthenticated) return;
      this.startOrExtendSession(minutes);
    },

    /**
     * 공용: 세션 시작/연장 처리
     */
    startOrExtendSession(minutes = 30) {
      this._logoutHandled = false;

      // ✅ 경고 상태 초기화
      this._warningShown = false;
      this.showSessionWarning = false;

      const expireAt = Date.now() + minutes * 60 * 1000; // 현재 시간에 30분을 추가해서 세션 만료 시간으로 지정
      this.sessionExpireAt = expireAt;

      // ✅ localStorage에 저장해서 새 탭에서도 유지
      localStorage.setItem(SESSION_EXPIRE_KEY, String(expireAt));

      // ✅ 다른 탭에 알려주기(즉시 동기화)
      localStorage.setItem(SESSION_SYNC_KEY, String(Date.now()));

      // ✅ 타이머
      this._updateSessionRemaining();
      this._startSessionTimer();
    },

    _startSessionTimer() {
      this._stopSessionTimer();

      // 1초마다 남은 시간 계산 + 만료 시 처리
      this._sessionTimerId = window.setInterval(() => {
        const raw = localStorage.getItem(SESSION_EXPIRE_KEY);
        const lsExpireAt = raw ? Number(raw) : 0;

        // ✅ 상한(지금부터 30분)
        // const maxAllowed = Date.now() + 30 * 60 * 1000;
        const maxAllowed = Date.now() + SESSION_MAX_MINUTES * 60 * 1000;

        // ✅ 조작했든 아니든, 초과면 무조건 자름
        if (lsExpireAt > maxAllowed) {
          localStorage.setItem(SESSION_EXPIRE_KEY, String(maxAllowed));
          localStorage.setItem(SESSION_SYNC_KEY, String(Date.now()));
          this.sessionExpireAt = maxAllowed; // store도 같이 맞춤
        } else {
          // (선택) localStorage 값이 유효하면 store도 동기화
          this.sessionExpireAt = lsExpireAt;
        }

        this._updateSessionRemaining();

        const remainMs = this.sessionExpireAt - Date.now(); // 남은 만료 시간
        const expired =
          this.sessionExpireAt > 0 && this.sessionExpireAt <= Date.now(); // 만료 여부

        // ✅ 만료 10분 전(= 600,000ms) 경고: 딱 1번만
        const warnMs = 10 * 60 * 1000;

        // ✅ "보고 있는 탭"에서만 10분 경고 1회
        const isActiveTab =
          document.visibilityState === "visible" && document.hasFocus();

        if (
          isActiveTab &&
          this.sessionExpireAt > 0 &&
          !this._warningShown &&
          remainMs > 0 &&
          remainMs <= warnMs
        ) {
          this._warningShown = true;
          this.showSessionWarning = true; // ✅ UI가 이거 보고 다이얼로그 띄움
        }

        // ✅ 인증 상태인데 만료되면 자동 로그아웃
        if (expired) {
          this._logoutAndGoLogin();
        }
      }, 1000);
    },

    _stopSessionTimer() {
      if (this._sessionTimerId) {
        clearInterval(this._sessionTimerId);
        this._sessionTimerId = null;
      }
    },

    _updateSessionRemaining() {
      // UI 화면에 보여질 값을 구하는 함수
      if (!this.sessionExpireAt) {
        // 로그인 세션이 없다면 0으로 초기화 후 리턴
        this.sessionRemainingMs = 0;
        return;
      }
      const remain = this.sessionExpireAt - Date.now(); // 세션 만료 시간에서 현재 시간을 빼서 만료 까지 남은 시간 할당
      this.sessionRemainingMs = Math.max(0, remain); // 0과 remain중 큰 숫자를 UI용 변수에 할당  (세션만료 시간보다 현재시간이 커서 음수가 나오는 버그 방지용)
    },

    _logoutAndGoLogin() {
      if (this._logoutHandled) return; // ✅ 중복 방지

      if (this.showSessionWarning) {
        sessionStorage.setItem("session-expired", "true");
      }

      this._warningShown = false;
      this.showSessionWarning = false;
      this._logoutHandled = true;
      this._stopSessionTimer();
      this.clearUser();

      const isKhrdc = window.location.hostname.includes('khrdc.org');
      const loginPath = isKhrdc ? '/login' : '/clogin';
      if (router.currentRoute.value.path !== loginPath) {
        router.push(loginPath);
      }
    },

    // 서버가 401(API 키 불일치 · 만료)을 돌려주면 바로 로그아웃하고 로그인 화면으로 (apiClient 응답 인터셉터)
    // 로그인하지 않은 상태(키 없음)의 401 은 로그인 화면 이동 대상이 아니다
    logoutOnUnauthorized() {
      if (!this.isUserLoggedIn) return;
      this._logoutAndGoLogin();
    },

    closeSessionWarning() {
      this.showSessionWarning = false;
    },
  },

  getters: {
    isUserLoggedIn: (state) => {
      // 로그인 여부 체크
      return state.user.APIKey && state.user.APIKey.trim() !== "";
    },

    isAuthenticated: (state) => {
      // 승인 사용자 여부 체크
      return (
        state.isUserLoggedIn &&
        (state.user.UserType === "N" || state.user.UserType === "S")
      );
    },

    isAdmin: (state) => {
      // 관리자 여부 체크
      return state.isUserLoggedIn && state.user.UserType === "S";
    },

    // ✅ 세션 만료 여부
    isSessionExpired: (state) => {
      return !state.sessionExpireAt || state.sessionExpireAt <= Date.now();
    },

    // ✅ 남은 시간 표시용 (mm:ss)
    sessionRemainingText: (state) => {
      const totalSec = Math.max(0, Math.floor(state.sessionRemainingMs / 1000));
      const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
      const ss = String(totalSec % 60).padStart(2, "0");
      return `${mm}:${ss}`;
    },
  },

  persist: {
    // storage: sessionStorage
    storage: localStorage,
  },
});

// 모든 탭에서 `sessionStorage` 삭제 이벤트 감지
// window.addEventListener('storage', (event) => {
//   if (event.key === 'U2EDC.sessionClear') {
//     sessionStorage.removeItem('userState'); // ✅ 모든 탭의 `sessionStorage` 삭제
//     // location.reload(); // ✅ 상태를 반영하기 위해 새로고침
//     if (router.currentRoute.value.path !== '/login') {
//       router.push('/login');
//     }
//   }
// });
